import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import logo from '../assets/Ntact.png';

// Styles
const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 10, fontFamily: 'Helvetica' },
  header: { flexDirection: 'column', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #ccc' },
  title: { fontSize: 16, fontWeight: 'bold', marginBottom: 5 },
  address: { fontSize: 10, marginBottom: 5 },
  reportPeriod: { fontSize: 10, marginBottom: 10, textAlign: 'center', width: '100%', marginTop: 10 },
  table: { border: '1px solid #ccc', marginBottom: 10 },
  tableRow: { flexDirection: 'row', borderBottom: '1px solid #ccc', textAlign: 'center' },
  tableHeader: { backgroundColor: '#f0f0f0', fontWeight: 'bold' },
  tableCell: { borderLeft: '1px solid #ccc', textAlign: 'left', fontSize: 8 },
  employeeCell: { width: '160px', padding: 5 },
  totalHoursCell: { width: '95px', padding: 2, textAlign: 'center', marginRight: '2px', paddingTop: 6 },
  totalPerDiemCell: { width: '100px', padding: 2, textAlign: 'center', marginRight: '2px', paddingTop: 6 },
  dayCell: (numDays) => ({
    width: numDays > 0 ? `${(100 / numDays).toFixed(2)}%` : '100%',
    padding: 2,
    textAlign: 'center',
    marginRight: '2px',
    paddingTop: 6,
  }),
  noData: { textAlign: 'center', padding: 20, fontSize: 12 },
  employeeDetails: { flexDirection: 'column' },
  employeeName: { fontWeight: 'bold', fontSize: 8 },
  classification: { fontSize: 7, color: '#555' },
  empId: {
    fontSize: 7,
    color: '#8B1818',
    backgroundColor: 'rgba(139, 24, 24, 0.1)',
    borderRadius: 10,
    padding: 2,
    alignSelf: 'flex-start',
  },
  tableSection: {
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
  },
  tableHeading: { fontWeight: 'bold', fontSize: 10, marginBottom: 4 },
  tableRowClient: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
  },
  tableHeaderClient: {
    flex: 1,
    fontWeight: 'bold',
    fontSize: 9,
    textAlign: 'center',
    paddingVertical: 6,
    color: '#333',
  },
  tableCellClient: {
    flex: 1,
    fontSize: 8,
    textAlign: 'center',
    padding: 6,
    color: '#444',
  },
  logo: {
    width: 70,
    height: 'auto',
    marginBottom: 5,
    alignSelf: 'center',
  },
});

const footerContainer = {
  position: 'absolute',
  bottom: 20,
  left: 60,
  flexDirection: 'row',
  alignItems: 'flex-end',
};

const textStyle = { fontSize: 10, color: '#333', fontWeight: 'bold' };
const lineStyle = { height: 1, width: 100, backgroundColor: '#333', marginLeft: 8, marginBottom: 2 };

const getDatesInRange = (start, end) => {
  const dates = [];
  let current = dayjs(start);
  const endDate = dayjs(end);
  while (current.isSameOrBefore(endDate)) {
    dates.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }
  return dates;
};

const timeToDecimal = (timeStr) => {
  if (!timeStr) return '0.00';
  const [hh, mm] = timeStr.split(':').map(Number);
  return (hh + (mm ? mm / 60 : 0)).toFixed(2);
};

const hasHoursForDate = (data, date) =>
  Object.values(data.emp_report || {}).some((emp) =>
    emp.activity?.some((act) => act.date === date && parseFloat(timeToDecimal(act.daily_hours)) > 0)
  );

const PDFDocument = ({
  data,
  startDate,
  endDate,
  selectedEmployee,
  selectedCompanyName,
  selectedClient,
  taskDescription,
  selectedSuggestions,
}) => {
  const actualDateRange = getDatesInRange(startDate, endDate);

  const hasDataForType = (datesToShow, type) =>
    Object.values(data.emp_report || {}).some((emp) => {
      const hasHours = datesToShow.some((date) =>
        emp.activity?.some((act) => act.date === date && parseFloat(timeToDecimal(act.daily_hours)) > 0)
      );
      if (!hasHours) return false;
      const empType = emp.activity?.[0]?.work_details?.[0]?.etype || 'EMP';
      return type === 'EMP' ? empType !== 'EQP' : empType === 'EQP';
    });

  const renderHeader = (dateRangeText, titleOverride) => (
    <View style={styles.header}>
      <Image src={logo} style={styles.logo} />
      <Text style={styles.title}>{titleOverride || 'WEEKLY LABOR RECAP'}</Text>
      <Text style={styles.address}>PO BOX 13328, ODESSA, TEXAS 79768</Text>
      <Text style={styles.address}>{selectedCompanyName}</Text>
      <Text style={styles.reportPeriod}>{dateRangeText}</Text>
    </View>
  );

  const renderClientInfo = () => (
    <>
      <Text style={styles.tableHeading}>Client Information</Text>
      <View style={styles.tableSection}>
        <View style={styles.tableRowClient}>
          {['Address', 'City', 'Client Name', 'Job Number', 'State', 'ZIP Code'].map((label, i) => (
            <Text key={i} style={[styles.tableHeaderClient, i !== 0 && { borderLeftWidth: 1, borderLeftColor: '#ccc' }]}>
              {label}
            </Text>
          ))}
        </View>
        <View style={styles.tableRowClient}>
          {[selectedClient.address, selectedClient.city, selectedClient.clientname, selectedSuggestions[0]?.job_num, selectedClient.state, selectedClient.zipcode].map((value, i) => (
            <Text key={i} style={[styles.tableCellClient, i !== 0 && { borderLeftWidth: 1, borderLeftColor: '#ccc' }]}>
              {value}
            </Text>
          ))}
        </View>
      </View>
    </>
  );

  const renderOrderedByAndTask = () => (
    <>
      <Text style={{ textAlign: 'left', fontSize: 10, fontWeight: 'bold' }}>Ordered By :</Text>
      <View style={{ marginTop: 6, marginBottom: 6, border: '0.7px solid #ccc', backgroundColor: '#f9f9f9', padding: 5, borderRadius: 4 }}>
        <Text>{selectedEmployee || '-'}</Text>
      </View>

      <Text style={{ textAlign: 'left', fontSize: 10, fontWeight: 'bold' }}>Task Description :</Text>
      <View style={{ marginTop: 6, marginBottom: 6, border: '0.7px solid #ccc', backgroundColor: '#f9f9f9', padding: 5, borderRadius: 4 }}>
        <Text>{taskDescription || '-'}</Text>
      </View>
    </>
  );

  const renderTableForDates = (datesToShow, type = 'EMP') => {
    const isEQP = type === 'EQP';
    const isSingleDay = datesToShow.length === 1;

    const rows = Object.values(data.emp_report || {}).filter((emp) => {
      const hasHours = datesToShow.some((date) =>
        emp.activity?.some((act) => act.date === date && parseFloat(timeToDecimal(act.daily_hours)) > 0)
      );
      if (!hasHours) return false;
      const empType = emp.activity?.[0]?.work_details?.[0]?.etype || 'EMP';
      return type === 'EMP' ? empType !== 'EQP' : empType === 'EQP';
    });

    const cumulativeHours = Array(datesToShow.length).fill(0);
    let cumulativeTotalHours = 0;
    let cumulativeTotalPerDiem = 0;

    rows.forEach((emp) => {
      cumulativeTotalHours += parseFloat(timeToDecimal(emp.total_hours));
      cumulativeTotalPerDiem += parseFloat(emp.total_perdim || 0);
      datesToShow.forEach((date, dIdx) => {
        const act = emp.activity?.find((a) => a.date === date);
        cumulativeHours[dIdx] += act?.daily_hours ? parseFloat(timeToDecimal(act.daily_hours)) : 0;
      });
    });

    return (
      <View style={styles.table} wrap>

        {/* HEADER */}
        <View style={[styles.tableRow, styles.tableHeader]} fixed>
          <Text style={[styles.tableCell, styles.employeeCell]}>
            {type === 'EMP' ? 'Employee' : 'Equipment'}
          </Text>

          {isSingleDay && (
            <Text style={[styles.tableCell, styles.totalHoursCell]}>
              {type === 'EMP' ? 'Cost Code' : 'Quantity'}
            </Text>
          )}

          <Text style={[styles.tableCell, styles.totalHoursCell]}>Total Hours</Text>

          {/* remove perdiem only for daily eqp */}
        {type !== 'EQP' && (
  <Text style={[styles.tableCell, styles.totalPerDiemCell]}>Total Per Diem</Text>
)}
          {/* daily eqp add unit column */}
          {isEQP && isSingleDay && (
            <Text style={[styles.tableCell, styles.totalPerDiemCell]}>Unit</Text>
          )}

          {datesToShow.map((date) => (
            <Text key={date} style={[styles.tableCell, styles.dayCell(datesToShow.length)]}>
              {dayjs(date).format('ddd')}
            </Text>
          ))}
        </View>

        {/* ROWS */}
        {rows.map((emp, rIdx) => (
          <View key={rIdx} style={styles.tableRow} wrap={false}>
            <View style={[styles.tableCell, styles.employeeCell, styles.employeeDetails]}>
              <Text style={styles.employeeName}>{emp.emp_name}</Text>
              {type === 'EMP' && <Text style={styles.classification}>{emp.classification_name}</Text>}
              <Text style={styles.empId}>{emp.emp_id}</Text>
            </View>

            {isSingleDay && (
              <Text style={[styles.tableCell, styles.totalHoursCell]}>
                {type === 'EMP'
                  ? emp.activity?.find((a) => a.date === datesToShow[0])?.work_details?.[0]?.cost_code_id || 'N/A'
                  : emp.activity?.find((a) => a.date === datesToShow[0])?.work_details?.[0]?.quantity || 'N/A'}
              </Text>
            )}

            <Text style={[styles.tableCell, styles.totalHoursCell]}>
              {timeToDecimal(emp.total_hours)}
            </Text>

            {/* perdiem */}
       {type !== 'EQP' && (
  <Text style={[styles.tableCell, styles.totalPerDiemCell]}>
    {isSingleDay
      ? `$${emp.activity?.find((a) => a.date === datesToShow[0])?.work_details?.[0]?.perdim || 0}`
      : `$${emp.total_perdim || 0}`}
  </Text>
)}

            {/* daily eqp unit */}
            {isEQP && isSingleDay && (
              <Text style={[styles.tableCell, styles.totalPerDiemCell]}>
                {emp.activity?.find((a) => a.date === datesToShow[0])?.work_details?.[0]?.unit || '-'}
              </Text>
            )}

            {/* days */}
            {datesToShow.map((date) => {
              const act = emp.activity?.find((a) => a.date === date);
              const unit = act?.work_details?.[0]?.unit;

              return (
                <View key={date} style={[styles.tableCell, styles.dayCell(datesToShow.length)]}>
                  <Text>
                    {act?.daily_hours ? `${timeToDecimal(act.daily_hours)} hrs` : '0.00 hrs'}
                  </Text>

                  {/* weekly eqp show unit */}
                  {isEQP && !isSingleDay && unit && (
                    <Text style={{ fontSize: 7, color: '#666' }}>{unit}</Text>
                  )}
                </View>
              );
            })}
          </View>
        ))}

        {/* TOTAL ROW */}
        <View style={[styles.tableRow, { backgroundColor: '#eee' }]} wrap={false}>
          <Text style={[styles.tableCell, styles.employeeCell, { fontWeight: 'bold' }]}>Total</Text>
          {isSingleDay && <Text style={[styles.tableCell, styles.totalHoursCell]}>-</Text>}

          <Text style={[styles.tableCell, styles.totalHoursCell, { fontWeight: 'bold' }]}>
            {cumulativeTotalHours.toFixed(2)}
          </Text>

          {/* total perdiem remove for daily eqp */}
          {!(isEQP && isSingleDay) && (
            <Text style={[styles.tableCell, styles.totalPerDiemCell, { fontWeight: 'bold' }]}>
              ${cumulativeTotalPerDiem.toFixed(2)}
            </Text>
          )}

          {isEQP && isSingleDay && (
            <Text style={[styles.tableCell, styles.totalPerDiemCell]}></Text>
          )}

          {datesToShow.map((date, dIdx) => (
            <Text key={date} style={[styles.tableCell, styles.dayCell(datesToShow.length), { fontWeight: 'bold' }]}>
              {cumulativeHours[dIdx].toFixed(2)} hrs
            </Text>
          ))}
        </View>
      </View>
    );
  };

  const summaryLabel = `Report - ${dayjs(startDate).format('YYYY-MM-DD')} to ${dayjs(endDate).format('YYYY-MM-DD')}`;

  return (
    <Document>
      {/* Summary – Labor */}
      <Page size="A4" style={styles.page}>
        {renderHeader(summaryLabel)}
        {renderClientInfo()}
        {renderOrderedByAndTask()}
        {hasDataForType(actualDateRange, 'EMP') && renderTableForDates(actualDateRange, 'EMP')}
        <View style={footerContainer}>
          <Text style={textStyle}>Approved By</Text>
          <View style={lineStyle} />
        </View>
      </Page>

      {/* Summary – Equipment */}
      {hasDataForType(actualDateRange, 'EQP') && (
        <Page size="A4" style={styles.page}>
          {renderHeader(summaryLabel, 'WEEKLY EQUIPMENT RECAP')}
          {renderClientInfo()}
          {renderOrderedByAndTask()}
          {renderTableForDates(actualDateRange, 'EQP')}
          <View style={footerContainer}>
            <Text style={textStyle}>Approved By</Text>
            <View style={lineStyle} />
          </View>
        </Page>
      )}

      {/* Daily */}
      {actualDateRange.map((date) => {
        if (!hasHoursForDate(data, date)) return null;
        const dayLabel = `Report - ${dayjs(date).format('dddd D MMMM YYYY')}`;
        const hasEmp = hasDataForType([date], 'EMP');
        const hasEqp = hasDataForType([date], 'EQP');

        return (
          <React.Fragment key={date}>
            {hasEmp && (
              <Page size="A4" style={styles.page}>
                {renderHeader(dayLabel)}
                {renderClientInfo()}
                {renderOrderedByAndTask()}
                {renderTableForDates([date], 'EMP')}
                <View style={footerContainer}>
                  <Text style={textStyle}>Approved By</Text>
                  <View style={lineStyle} />
                </View>
              </Page>
            )}

            {hasEqp && (
              <Page size="A4" style={styles.page}>
                {renderHeader(dayLabel, 'WEEKLY EQUIPMENT RECAP')}
                {renderClientInfo()}
                {renderOrderedByAndTask()}
                {renderTableForDates([date], 'EQP')}
                <View style={footerContainer}>
                  <Text style={textStyle}>Approved By</Text>
                  <View style={lineStyle} />
                </View>
              </Page>
            )}
          </React.Fragment>
        );
      })}
    </Document>
  );
};

export default PDFDocument;